
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  locale: null,
  currency: null,
  darkMode: false,
  loginUser: {},
  isLogin: false,
  lastSearch: [],
  hasClosedDownloadSection: false,
};

export const loginconfigSlice = createSlice({
  name: 'loginconfig',
  initialState,
  reducers: {
    resetLoginConfigState: () => initialState,
    changeAppLocale: (state, action) => {
      state.locale = action.payload;
    },
    setAppLocaleOnAppStart: (state, action) => {
      state.locale = action.payload;
    },
    changeAppCurrency: (state, action) => {
      state.currency = action.payload;
    },
    setDarkMode: (state, action) => {
      state.darkMode = action.payload;
    },
    setLoginUser: (state, action) => {
      state.loginUser = action.payload;
    },
    setIsLogin: (state, action) => {
      state.isLogin = action.payload;
    },
    setLastSearch: (state, action) => {
      // Validate the incoming data structure
      const isValidSearch = (data) => {
        const isGeoCoordinate = (str) => /^\s*-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?\s*$/.test(str);

        return (
          data &&
          Array.isArray(data.FlightSegmentList) &&
          data.FlightSegmentList.length === 2 &&
          // Inbound segment (FlightSegmentList[0])
          data.FlightSegmentList[0]?.Departure?.CodeList &&
          Array.isArray(data.FlightSegmentList[0].Departure.CodeList) &&
          data.FlightSegmentList[0].Departure.CodeList.length > 0 &&
          data.FlightSegmentList[0].Departure.CodeList.every(item => !isGeoCoordinate(item)) &&
          data.FlightSegmentList[0]?.Departure?.Date &&
          data.FlightSegmentList[0]?.Arrival?.CodeList &&
          Array.isArray(data.FlightSegmentList[0].Arrival.CodeList) &&
          data.FlightSegmentList[0].Arrival.CodeList.length > 0 &&
          data.FlightSegmentList[0].Arrival.CodeList.every(item => !isGeoCoordinate(item)) &&
          // Outbound segment (FlightSegmentList[1])
          data.FlightSegmentList[1]?.Departure?.CodeList &&
          Array.isArray(data.FlightSegmentList[1].Departure.CodeList) &&
          data.FlightSegmentList[1].Departure.CodeList.length > 0 &&
          data.FlightSegmentList[1].Departure.CodeList.every(item => !isGeoCoordinate(item)) &&
          data.FlightSegmentList[1]?.Departure?.Date &&
          data.FlightSegmentList[1]?.Arrival?.IataCodeList &&
          Array.isArray(data.FlightSegmentList[1].Arrival.IataCodeList) &&
          data.FlightSegmentList[1].Arrival.IataCodeList.length > 0 &&
          data.FlightSegmentList[1].Arrival.IataCodeList.every(item => !isGeoCoordinate(item)) &&
          // Passenger list
          Array.isArray(data.PassengerAgeRoomItemList) &&
          data.PassengerAgeRoomItemList.length > 0
        );
      };

      // Only proceed if the payload is valid
      if (!isValidSearch(action.payload)) {
        return;
      }

      let newArray = Array.isArray(state.lastSearch) ? [...state.lastSearch] : [];

      // Helper to create comparable string without id and date
      const toCleanString = (obj) => JSON.stringify(
        Object.fromEntries(
          Object.entries(obj).filter(([key]) => key !== 'id' && key !== 'date')
        )
      );

      // Find index of similar search (excluding id and date)
      const similarIndex = newArray.findIndex(search => 
        toCleanString(action.payload) === toCleanString(search)
      );

      if (similarIndex !== -1) {
        // If similar search exists (full object match excluding id/date), remove it
        newArray.splice(similarIndex, 1);
      } else {
        // Additional logic: if the incoming data's four CodeList fields match an existing saved search,
        // update that saved search with the new payload and move it to the first index.
        const getLists = (obj) => ({
          inDeparture: obj?.FlightSegmentList?.[0]?.Departure?.CodeList ?? [],
          inArrival: obj?.FlightSegmentList?.[0]?.Arrival?.CodeList ?? [],
          outDeparture: obj?.FlightSegmentList?.[1]?.Departure?.CodeList ?? [],
          outArrival: obj?.FlightSegmentList?.[1]?.Arrival?.IataCodeList ?? []
        });

        const extractPrefix = (str) => {
          const match = ('' + str).match(/^\[([^\]]+)\]/);
          if (match) {
            const content = match[1];
            return content.substring(0, 3).toLowerCase();
          }
          return ('' + str).trim().substring(0, 3).toLowerCase();
        };

        const normalize = (arr) => (Array.isArray(arr) ? arr.map(i => extractPrefix(i)).sort() : []);

        const listsEqual = (a, b) => {
          const na = normalize(a);
          const nb = normalize(b);
          if (na.length !== nb.length) return false;
          for (let i = 0; i < na.length; i++) {
            if (na[i] !== nb[i]) return false;
          }
          return true;
        };

        const incomingLists = getLists(action.payload);

        const matchIndex = newArray.findIndex(saved => {
          const savedLists = getLists(saved);
          return (
            listsEqual(incomingLists.inDeparture, savedLists.inDeparture) &&
            listsEqual(incomingLists.inArrival, savedLists.inArrival) &&
            listsEqual(incomingLists.outDeparture, savedLists.outDeparture) &&
            listsEqual(incomingLists.outArrival, savedLists.outArrival)
          );
        });

        if (matchIndex !== -1) {
          // Remove the matched item so we can re-add it to the front with updated payload
          newArray.splice(matchIndex, 1);
        }
      }

      // Add the new search at the beginning
      newArray.unshift(action.payload);

      // Keep only the first 3 items
      if (newArray.length > 3) {
        newArray = newArray.slice(0, 3);
      }

      state.lastSearch = newArray;
    },
    setHasClosedDownloadSection: (state, action) => {
      state.hasClosedDownloadSection = action.payload;
    },
  },
});

// This will return complete state of this slice.
export const getLoginConfig = (state) => state.loginconfig;

// Action creators are generated for each case reducer function
export const {
  resetLoginConfigState,
  changeAppLocale,
  setAppLocaleOnAppStart,
  changeAppCurrency,
  setDarkMode,
  setLoginUser,
  setIsLogin,
  setLastSearch,
  setHasClosedDownloadSection
} = loginconfigSlice.actions;

export default loginconfigSlice.reducer;
